import { useEffect, useState } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { useSelector } from "react-redux";

import { COLORS } from "../../../../../theme/colors";
import { tenantPaymentReportBars } from "../sample";
import { ShaDowBox } from "../../../../../components/boxes";
import { LabelBarChart } from "../../../../../components/charts";
import { GraphFooter, NoDataCard } from "../../../../../components/parts";
import { getData, getPercent, sumFromList } from "../../../../../services";

const useStyles = makeStyles(() => ({
  box: {
    marginTop: 5,
    height: 240,
  },
}));

const graphItems = [
  {
    name: "Paid",
    color: COLORS.green,
  },
  {
    name: "2 Mon",
    suffix: "delay",
    color: COLORS.red,
  },
  {
    name: "6 Mon",
    suffix: "delay",
    color: COLORS.internationalOrange,
  },
  {
    name: "6-12 Mon",
    suffix: "delay",
    color: COLORS.barnRed,
  },
  {
    name: "> 1y",
    suffix: "delay",
    color: COLORS.black,
  },
];

export const TenantPaymentsReport = ({ building, complex }) => {
  const classes = useStyles();
  const [chartData, setChartData] = useState([]);
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);
  const { cluster, segments, selectedCountries } = useSelector((state) => state.selectCSC);

  let url = `/praas/v1/buildings/payments-aging/`;

  if (building?.id) {
    url = `/praas/v2/buildings/${building.id}/tenants/payments-aging/`;
  } else if (complex?.id) {
    url = `/praas/v2/complexes/${complex.id}/tenants/payments-aging/`;
  }

  useEffect(() => {
    getData(url, ({ delay_percent: delayPercent, invoices, total }) => {
      const delaySum = sumFromList(Object.values(delayPercent));
      const invoiceSum = sumFromList(Object.values(invoices));
      const totalSum = sumFromList(Object.values(total));
      if (delaySum || invoiceSum || totalSum) {
        setChartData([
          {
            name: "Delay %",
            paying: getPercent(delayPercent.paid, delaySum),
            two_month: getPercent(delayPercent.billing_delay_2_month, delaySum),
            six_month: getPercent(delayPercent.billing_delay_6_month, delaySum),
            six_year: getPercent(delayPercent.billing_delay_6_12_month, delaySum),
            year: getPercent(delayPercent.billing_delay_more_year, delaySum),
            label: "1",
          },
          {
            name: "Invoices",
            paying: getPercent(invoices.paid, invoiceSum),
            two_month: getPercent(invoices.billing_delay_2_month, invoiceSum),
            six_month: getPercent(invoices.billing_delay_6_month, invoiceSum),
            six_year: getPercent(invoices.billing_delay_6_12_month, invoiceSum),
            year: getPercent(invoices.billing_delay_more_year, invoiceSum),
            label: "2",
          },
          {
            name: `Total ${currencySymbol}`,
            paying: getPercent(total.paid, totalSum),
            two_month: getPercent(total.billing_delay_2_month, totalSum),
            six_month: getPercent(total.billing_delay_6_month, totalSum),
            six_year: getPercent(total.billing_delay_6_12_month, totalSum),
            year: getPercent(total.billing_delay_more_year, totalSum),
            label: "3",
          },
        ]);
      }
    });

    return () => setChartData([]);
  }, [building?.id, complex?.id, cluster, segments, selectedCountries]);

  if (!chartData.length) {
    return (
      <NoDataCard
        title="Tenants Payments - aging report"
        footerNames={["Paid", "2 Mon delay", "6 Mon delay", "6-12 Mon delay", "> 1y delay"]}
      />
    );
  }

  return (
    <ShaDowBox fullHeight>
      <Box style={{ padding: 24 }}>
        <Typography variant="h4">Tenants Payments - aging report</Typography>
        <Box className={classes.box}>
          <LabelBarChart
            top={30}
            bottom={30}
            height={240}
            chartData={chartData}
            bars={tenantPaymentReportBars}
            barSize={24}
            hideYAxis
            hideXAxis
            hideTooltip
            hideCartesianGrid
            hideTopLabel
            hideReferenceLine
          />
        </Box>
        <GraphFooter
          style={{ marginTop: 13 }}
          items={graphItems}
          typographyStyle={{
            fontSize: 12,
          }}
        />
      </Box>
    </ShaDowBox>
  );
};
